import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, Button, Form, Spinner, Badge, Table, Alert } from 'react-bootstrap';
import { Star, Package, RefreshCw, X, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMatchedProducts } from '../../../apps/hooks/sourcing/useMatchedProducts';
import { CommandName } from '../../../infrastructure/BatchClient';
import type { CpGpMatchedProduct } from '../../../models/Coupang';
import { ScrapingStatus, ShippingType } from '../../../models/Coupang';
import ServerFilters from './components/ServerFilters';
import { cpGpMatchClient } from '../../../infrastructure/CpGpMatchClient';
import { glowpickClient } from '../../../infrastructure/GlowpickClient';
import { cmWorkflowClient } from '../../../infrastructure/CMWorkflowClient';
import { getCMWorkflowStatusColor } from '../../../components/ui/badge/StatusBadge';
import { normalizeWorkflowStatus } from '../../../models/CatalogManagement';

import { ROUTES } from '../../../routes/routes';

const MatchedProductsPage = () => {
  const navigate = useNavigate();
  const { category, subcategory } = useParams<{
    category?: string;
    subcategory?: string;
  }>();
  const urlParamsProcessed = useRef(false);
  const scrollRestorationAttempted = useRef(false);
  const previousCategoryRef = useRef<string>('');
  const unmatchedItemsInitialized = useRef(false);

  // Track unmatched items (show "Unmatched" text without removing from list)
  const [unmatchedItems, setUnmatchedItems] = useState<Set<string>>(() => {
    try {
      const cached = sessionStorage.getItem('matchedProducts.unmatchedItems');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.info('📦 Restored unmatched items from cache:', parsed.length, 'items');
        return new Set(parsed);
      }
    } catch (e) {
      console.warn('❌ Failed to restore unmatched items:', e);
    }
    return new Set();
  });

  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [isBulkUnmatching, setIsBulkUnmatching] = useState(false);

  // Save unmatched items to sessionStorage whenever it changes
  useEffect(() => {
    const itemsArray = Array.from(unmatchedItems);
    sessionStorage.setItem('matchedProducts.unmatchedItems', JSON.stringify(itemsArray));
    console.info('💾 Saved unmatched items to cache:', itemsArray.length, 'items');
  }, [unmatchedItems]);

  const {
    matchedProducts,
    loading,
    isExporting,
    pagination,
    filters,
    availableProfiles,
    availableCategories,
    availableSubcategories,
    loadMore,
    refresh,
    setItemsPerPage,
    updateFilter,
    clearClientFilters,
    exportToGoogleSheet,
    getRunningJobs,
    categoriesLoading,
    fetchMultipleSubcategories,
  } = useMatchedProducts();

  // Query for running export jobs
  const runningExportJobsQuery = useQuery({
    queryKey: ['runningExportJobs', CommandName.EXPORT_CP_GP_MATCHED],
    queryFn: () => getRunningJobs(),
    refetchInterval: (data) => {
      // If there are running jobs, check every 15 seconds
      // If no running jobs, check every 60 seconds (less frequent)
      return data && Array.isArray(data) && data.length > 0 ? 15000 : 60000;
    },
    refetchIntervalInBackground: false, // Don't poll when tab is not active/focused
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  const { data: runningExportJobs } = runningExportJobsQuery;
  const hasRunningExportJob = Boolean(runningExportJobs && runningExportJobs.length > 0);

  // Handle URL parameters for category/subcategory
  useEffect(() => {
    // Only process URL parameters once
    if (urlParamsProcessed.current) {
      return;
    }

    // Add a delay to ensure the component is fully initialized
    const timeoutId = setTimeout(() => {
      if (category) {
        // Set category and subcategory filters from URL
        const decodedCategory = decodeURIComponent(category);
        const decodedSubcategory = subcategory ? decodeURIComponent(subcategory) : '';

        console.info('🔍 URL params:', { decodedCategory, decodedSubcategory });

        // Check if we have cached filters - if so, prefer cache over incomplete URL
        const cachedFilters = sessionStorage.getItem('matchedProducts.filters');
        if (cachedFilters) {
          try {
            const parsed = JSON.parse(cachedFilters);
            console.info('📦 Cached filters:', { category: parsed.category, subcategory: parsed.subcategory });

            // If cache has the same category and has a subcategory, but URL doesn't, use cache
            if (parsed.category === decodedCategory && parsed.subcategory && !decodedSubcategory) {
              console.info('✅ Using cached filters (more complete than URL)');
              urlParamsProcessed.current = true;
              return;
            }

            // If cache matches URL exactly, don't update (prevents unnecessary re-renders)
            if (parsed.category === decodedCategory && parsed.subcategory === decodedSubcategory) {
              console.info('✅ Cached filters match URL - skipping update');
              urlParamsProcessed.current = true;
              return;
            }
          } catch (e) {
            console.warn('❌ Failed to parse cached filters:', e);
          }
        }

        console.info('🔄 Updating filters from URL');
        // Set category first
        updateFilter('category', decodedCategory);

        // If subcategory exists, set it after a small delay
        if (decodedSubcategory) {
          setTimeout(() => {
            updateFilter('subcategory', decodedSubcategory);
          }, 100);
        }

        urlParamsProcessed.current = true;
      }
    }, 100); // Small delay to ensure component is initialized

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Reset the flag when URL parameters actually change
  useEffect(() => {
    // Don't run this on initial mount
    if (!urlParamsProcessed.current) {
      return;
    }

    // Reset the flag when URL parameters actually change
    urlParamsProcessed.current = false;
  }, [category, subcategory]);

  // Reset scroll restoration flag when component mounts (when coming back from detail page)
  useEffect(() => {
    scrollRestorationAttempted.current = false;
    console.info('🔄 Reset scroll restoration flag on mount');
  }, []);

  // Save scroll position before navigating away
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('matchedProducts.scrollY', window.scrollY.toString());
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Restore scroll position when data is loaded
  useEffect(() => {
    if (!loading && matchedProducts.length > 0 && !scrollRestorationAttempted.current) {
      scrollRestorationAttempted.current = true;
      const savedScrollY = sessionStorage.getItem('matchedProducts.scrollY');
      if (savedScrollY) {
        console.info('📜 Restoring scroll position:', savedScrollY);
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollY, 10));
        }, 100);
      }
    }
  }, [loading, matchedProducts.length]);

  // Clear unmatched items when category actually changes (not on initial mount)
  useEffect(() => {
    // Skip on first mount - let the restored cache be used
    if (!unmatchedItemsInitialized.current) {
      unmatchedItemsInitialized.current = true;
      previousCategoryRef.current = filters.category;
      console.info('🎬 Initialized unmatched items tracking for category:', filters.category);
      return;
    }

    // Only clear if category actually changed
    if (previousCategoryRef.current !== filters.category) {
      console.info(
        '🗑️ Category changed from',
        previousCategoryRef.current,
        'to',
        filters.category,
        '- clearing unmatched items cache',
      );
      setUnmatchedItems(new Set());
      previousCategoryRef.current = filters.category;
    }
  }, [filters.category]);

  // Wrapper function for refresh that also clears unmatched items
  const handleRefresh = () => {
    console.info('🔄 Refreshing and clearing unmatched items');
    setUnmatchedItems(new Set());
    refresh();
  };

  // Handle checkbox toggle
  const handleCheckboxToggle = (matchKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(matchKey)) {
        next.delete(matchKey);
      } else {
        next.add(matchKey);
      }
      return next;
    });
  };

  // Handle select all
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allKeys = new Set(matchedProducts.map((m) => `${m.cpProductId}-${m.cpItemId}`));
      setSelectedProducts(allKeys);
    } else {
      setSelectedProducts(new Set());
    }
  };

  // Handle individual unmatch
  const handleIndividualUnmatch = async (match: CpGpMatchedProduct) => {
    const matchKey = `${match.cpProductId}-${match.cpItemId}`;

    if (
      !confirm(
        `Are you sure you want to unmatch this product?\n\nGP: ${match.gpDetailedProduct?.name || 'Unknown'}\nCP: ${match.cpDetailedProduct?.name || 'Unknown'}`,
      )
    ) {
      return;
    }

    // Mark as unmatched immediately (show "Unmatched" text)
    setUnmatchedItems((prev) => new Set(prev).add(matchKey));

    // Delete from DDB in background
    try {
      console.info(`🗑️ Deleting CP-GP match from DDB: ${match.cpProductId}-${match.cpItemId}`);
      const result = await cpGpMatchClient.deleteMatch(match.cpProductId, match.cpItemId);

      if (result.success) {
        console.info(`✅ Successfully deleted CP-GP match from DDB: ${match.cpProductId}-${match.cpItemId}`);

        // Update Glowpick metadata if available
        if (match.gpProductId && match.gpDetailedProduct?.metadata?.cpProducts) {
          console.info(`📝 Updating Glowpick metadata for GP product: ${match.gpProductId}`);
          const updatedCpProducts = match.gpDetailedProduct.metadata.cpProducts.filter(
            (cp) => cp.cpUniqueId !== matchKey,
          );

          const updatedMetadata = {
            cpProducts: updatedCpProducts,
            notFoundInCoupang: false,
          };

          await glowpickClient.updateProductMetadata(match.gpProductId, updatedMetadata);
          console.info(`✅ Successfully updated Glowpick metadata for GP product: ${match.gpProductId}`);
        }

        console.info('✅ Unmatched successfully - item will disappear on next refresh');
      } else {
        console.error(`❌ Failed to delete CP-GP match from DDB ${matchKey}:`, result.message);
        // Remove from unmatched set on failure
        setUnmatchedItems((prev) => {
          const next = new Set(prev);
          next.delete(matchKey);
          return next;
        });
        alert(`Failed to unmatch: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`❌ Error deleting CP-GP match from DDB ${matchKey}:`, error);
      // Remove from unmatched set on error
      setUnmatchedItems((prev) => {
        const next = new Set(prev);
        next.delete(matchKey);
        return next;
      });
      alert(`Error unmatching product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle bulk unmatch
  const handleBulkUnmatch = async () => {
    if (selectedProducts.size === 0) {
      return;
    }

    if (!confirm(`Are you sure you want to unmatch ${selectedProducts.size} selected product(s)?`)) {
      return;
    }

    setIsBulkUnmatching(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const matchKey of selectedProducts) {
        const parts = matchKey.split('-');
        const cpProductId = parts[0];
        const cpItemId = parts[1];

        if (!cpProductId || !cpItemId) {
          continue;
        }

        const match = matchedProducts.find((m) => m.cpProductId === cpProductId && m.cpItemId === cpItemId);

        if (!match) {
          continue;
        }

        try {
          console.info(`🗑️ Deleting CP-GP match from DDB: ${cpProductId}-${cpItemId}`);
          const result = await cpGpMatchClient.deleteMatch(cpProductId, cpItemId);

          if (result.success) {
            successCount++;
            console.info(`✅ Successfully deleted CP-GP match from DDB: ${cpProductId}-${cpItemId}`);

            // Update Glowpick metadata if available
            if (match.gpProductId && match.gpDetailedProduct?.metadata?.cpProducts) {
              console.info(`📝 Updating Glowpick metadata for GP product: ${match.gpProductId}`);
              const updatedCpProducts = match.gpDetailedProduct.metadata.cpProducts.filter(
                (cp) => cp.cpUniqueId !== matchKey,
              );

              const updatedMetadata = {
                cpProducts: updatedCpProducts,
                notFoundInCoupang: false,
              };

              await glowpickClient.updateProductMetadata(match.gpProductId, updatedMetadata);
              console.info(`✅ Successfully updated Glowpick metadata for GP product: ${match.gpProductId}`);
            }
          } else {
            failCount++;
            console.error(`❌ Failed to delete CP-GP match from DDB ${matchKey}:`, result.message);
          }
        } catch (error) {
          failCount++;
          console.error(`❌ Error deleting CP-GP match from DDB ${matchKey}:`, error);
        }
      }

      // Clear selection
      setSelectedProducts(new Set());

      // Show result message and refresh
      if (successCount > 0) {
        alert(
          `✓ Successfully unmatched ${successCount} product(s)${failCount > 0 ? `\n✗ Failed to unmatch ${failCount} product(s)` : ''}`,
        );
        // Refresh to show updated list
        refresh();
      } else {
        alert(`✗ Failed to unmatch all selected products`);
      }
    } finally {
      setIsBulkUnmatching(false);
    }
  };

  const handleRowClick = (match: CpGpMatchedProduct, event: React.MouseEvent) => {
    const matchKey = `${match.cpProductId}-${match.cpItemId}`;

    // Don't navigate if item is unmatched
    if (unmatchedItems.has(matchKey)) {
      return;
    }

    const targetUrl = ROUTES.sourcing.matchedDetail(match.cpProductId, match.cpItemId);

    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(targetUrl, '_blank');
      return;
    }

    navigate(targetUrl, {
      state: { productData: match },
    });
  };

  const handleExportToGoogleSheet = async (subcategories?: string[]) => {
    try {
      await exportToGoogleSheet(subcategories);
      await runningExportJobsQuery.refetch();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleLoadData = async (selectedSubcategories: string[]) => {
    // Load data based on subcategory selection
    // - Single subcategory: Use normal filter
    // - Multiple subcategories: Fetch all selected subcategories and combine results
    // - No subcategories: Clear filter to show all

    if (selectedSubcategories.length === 1) {
      // Load data for single subcategory using normal filter
      updateFilter('subcategory', selectedSubcategories[0]!);
      console.info(`📊 Loading data for subcategory: ${selectedSubcategories[0]}`);
    } else if (selectedSubcategories.length > 1) {
      // Load data for multiple subcategories by making N API calls
      console.info(`📊 Loading data for ${selectedSubcategories.length} subcategories:`, selectedSubcategories);
      await fetchMultipleSubcategories(selectedSubcategories);
    } else {
      // If no subcategories selected, clear the subcategory filter to show all
      updateFilter('subcategory', '');
      console.info('📊 Loading all data for category');
    }
  };

  const handleDeleteCMWorkflow = async (gpi: string, cppi: string) => {
    if (
      !confirm(
        `Are you sure you want to delete this CM workflow?\n\nGPI: ${gpi}\nCPPI: ${cppi}\n\nThis action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await cmWorkflowClient.deleteCMWorkflow(gpi, cppi);
      console.info('✅ CM workflow deleted from API');

      try {
        await glowpickClient.deleteCpProductsFromGlowpickMetadata(gpi, cppi);
      } catch (metadataError) {
        console.warn('⚠️ Failed to update Glowpick metadata (non-critical):', metadataError);
      }

      console.info('✅ CM workflow deleted successfully');
      refresh();
    } catch (error) {
      console.error('❌ Failed to delete CM workflow:', error);
      alert(`Failed to delete CM workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Container fluid className="py-0">
      <div className="mb-1 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="h3 mb-1">GP-CP Matched List</h4>
          {selectedProducts.size > 0 && (
            <Badge bg="primary" className="mt-2">
              {selectedProducts.size} selected
            </Badge>
          )}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {/* Bulk unmatch button */}
          {selectedProducts.size > 0 && (
            <Button variant="danger" size="sm" onClick={handleBulkUnmatch} disabled={isBulkUnmatching}>
              {isBulkUnmatching ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Unmatching...
                </>
              ) : (
                <>
                  <X size={16} className="me-1" />
                  Unmatch ({selectedProducts.size})
                </>
              )}
            </Button>
          )}

          {/* Refresh button */}
          <Button variant="outline-primary" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </Button>

          {/* Items per page selector */}
          <Form.Select
            size="sm"
            value={pagination.itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={200}>200 per page</option>
            <option value={300}>300 per page</option>
            <option value={500}>500 per page</option>
          </Form.Select>
        </div>
      </div>

      {/* Export Status Alert */}
      {hasRunningExportJob && (
        <Alert variant="warning" className="mb-3">
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <strong>Export in Progress:</strong> CP-GP matched products are being exported to Google Sheets. This may
            take a few minutes to complete.
          </div>
        </Alert>
      )}

      {/* Filters */}
      <ServerFilters
        filters={filters}
        availableCategories={availableCategories}
        availableSubcategories={availableSubcategories || []}
        availableProfiles={availableProfiles}
        onFilterChange={updateFilter}
        categoriesLoading={categoriesLoading}
        isExporting={isExporting}
        hasRunningExportJob={hasRunningExportJob}
        onExport={handleExportToGoogleSheet}
        onLoadData={handleLoadData}
        onClearFilters={clearClientFilters}
      />

      <Card>
        <Card.Body className="p-0">
          {loading && matchedProducts.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Loading matched products...</p>
            </div>
          ) : matchedProducts.length === 0 ? (
            <div className="text-center py-5">
              <Package size={48} className="text-muted mb-3" />
              <h5>No matched products found</h5>
              <p className="text-muted">No Glowpick products have been matched with Coupang products yet.</p>
            </div>
          ) : (
            <>
              {/* Products Table */}
              <div className="flex-grow-1 overflow-auto">
                <Table className="mb-0" bordered>
                  <style>
                    {`
                    .glowpick-row-hovered {
                      background-color: rgba(0, 123, 255, 0.05) !important;
                      border-left: 3px solid #007bff !important;
                    }
                    .glowpick-row-focused {
                      outline: 2px solid rgba(0, 123, 255, 0.5) !important;
                      outline-offset: -2px !important;
                      background-color: rgba(0, 123, 255, 0.08) !important;
                    }
                  `}
                  </style>
                  <thead>
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedProducts.size === matchedProducts.length && matchedProducts.length > 0}
                          onChange={handleSelectAll}
                          style={{ marginBottom: 0 }}
                        />
                      </th>
                      <th style={{ width: '30px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>#</th>
                      <th style={{ width: '120px', fontSize: '0.8rem', fontWeight: '600' }}>Operator</th>
                      <th style={{ width: '110px', fontSize: '0.8rem', fontWeight: '600' }}>Matched Date</th>
                      <th style={{ width: '110px', fontSize: '0.8rem', fontWeight: '600' }}>Scraped Date</th>
                      <th style={{ width: '100px', fontSize: '0.8rem', fontWeight: '600' }}>Scraping Status</th>
                      <th style={{ width: '120px', fontSize: '0.8rem', fontWeight: '600' }}>CM Status</th>
                      <th style={{ width: '110px', fontSize: '0.8rem', fontWeight: '600' }}>CM Updated Date</th>
                      <th style={{ width: '200px', minWidth: '200px', fontSize: '0.8rem', fontWeight: '600' }}>
                        Keyword
                      </th>
                      <th style={{ width: '50%', fontSize: '0.8rem', fontWeight: '600' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#eb3d48', letterSpacing: '1px' }}>
                          GLOWPICK
                        </span>
                      </th>
                      <th style={{ width: '50%', fontSize: '0.8rem', fontWeight: '600' }}>
                        <img src="/assets/coupang-logo.png" alt="Coupang" style={{ height: '20px', width: 'auto' }} />
                      </th>
                      <th style={{ width: '140px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchedProducts.map((match: CpGpMatchedProduct, index: number) => {
                      // Skip null/undefined items safely
                      if (!match) {
                        return null;
                      }

                      const isHovered = false; // We'll manage hover states manually
                      const isFocused = false;

                      // Get operator information
                      const cpUniqueId = `${match.cpProductId}-${match.cpItemId}`;
                      const cpProducts = match.gpDetailedProduct?.metadata?.cpProducts;
                      const matchingProduct = cpProducts?.find((product) => product.cpUniqueId === cpUniqueId);
                      const operator = matchingProduct?.operator || 'N/A';
                      const operatedAt = matchingProduct?.operatedAt
                        ? new Date(matchingProduct.operatedAt).toISOString().split('T')[0]
                        : 'N/A';

                      const matchKey = `${match.cpProductId}-${match.cpItemId}`;
                      const isUnmatched = unmatchedItems.has(matchKey);

                      return (
                        <tr
                          key={`${match.cpProductId}-${match.cpItemId}-${index}`}
                          className={`${isHovered && !isUnmatched ? 'glowpick-row-hovered' : ''} ${isFocused ? 'glowpick-row-focused' : ''}`}
                          style={{
                            cursor: isUnmatched ? 'not-allowed' : 'pointer',
                            transition: 'all 0.05s ease-in-out',
                            opacity: isUnmatched ? 0.5 : 1,
                            backgroundColor: isUnmatched ? '#f8f9fa' : undefined,
                          }}
                          onMouseEnter={(e) => {
                            if (!isUnmatched) {
                              e.currentTarget.classList.add('glowpick-row-hovered');
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.classList.remove('glowpick-row-hovered');
                          }}
                          onClick={(e) => handleRowClick(match, e)}
                          onMouseDown={(e) => {
                            if (e.button === 1 && !isUnmatched) {
                              e.preventDefault();
                              handleRowClick(match, e);
                            }
                          }}
                        >
                          {/* Checkbox */}
                          <td
                            style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle', padding: '8px 4px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={selectedProducts.has(matchKey)}
                              onChange={(e) => handleCheckboxToggle(matchKey, e)}
                              disabled={isUnmatched}
                              style={{ marginBottom: 0 }}
                            />
                          </td>

                          {/* Row Number */}
                          <td
                            style={{ width: '30px', textAlign: 'center', verticalAlign: 'middle', padding: '8px 4px' }}
                          >
                            <small className="text-muted" style={{ fontSize: '0.65rem', fontWeight: '400' }}>
                              {index + 1}
                            </small>
                          </td>

                          {/* Operator */}
                          <td style={{ width: '120px', verticalAlign: 'middle', padding: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#333333' }}>{operator}</span>
                          </td>

                          {/* Matched Date */}
                          <td style={{ width: '110px', verticalAlign: 'middle', padding: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#666666' }}>{operatedAt}</span>
                          </td>

                          {/* Scraped Date */}
                          <td style={{ width: '110px', verticalAlign: 'middle', padding: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#666666' }}>
                              {match.updatedAt ? new Date(match.updatedAt).toISOString().split('T')[0] : 'N/A'}
                            </span>
                          </td>

                          {/* Scraping Status */}
                          <td style={{ width: '100px', verticalAlign: 'middle', padding: '8px' }}>
                            <Badge
                              bg={
                                match.scrapingStatus === ScrapingStatus.COMPLETED
                                  ? 'success'
                                  : match.scrapingStatus === ScrapingStatus.FAILED
                                    ? 'danger'
                                    : 'warning'
                              }
                              style={{ fontSize: '0.65rem' }}
                            >
                              {match.scrapingStatus}
                            </Badge>
                          </td>

                          {/* CM Status */}
                          <td style={{ width: '120px', verticalAlign: 'middle', padding: '8px' }}>
                            <div className="d-flex align-items-center gap-1">
                              {match.cmStatus ? (
                                <>
                                  {(() => {
                                    const normalizedCmStatus = normalizeWorkflowStatus(match.cmStatus) ?? match.cmStatus;
                                    return (
                                      <Badge
                                        bg={getCMWorkflowStatusColor(normalizedCmStatus)}
                                        style={{ fontSize: '0.65rem' }}
                                      >
                                        {normalizedCmStatus}
                                      </Badge>
                                    );
                                  })()}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCMWorkflow(
                                        match.gpProductId,
                                        `${match.cpProductId}-${match.cpItemId}`,
                                      );
                                    }}
                                    title="Delete CM Workflow"
                                    style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </>
                              ) : (
                                <span style={{ fontSize: '0.75rem', color: '#999999' }}>N/A</span>
                              )}
                            </div>
                          </td>

                          {/* CM Status Updated Date */}
                          <td style={{ width: '110px', verticalAlign: 'middle', padding: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#666666' }}>
                              {match.updatedAt ? new Date(match.updatedAt).toISOString().split('T')[0] : 'N/A'}
                            </span>
                          </td>

                          {/* Keyword */}
                          <td style={{ width: '200px', minWidth: '200px', verticalAlign: 'middle', padding: '8px' }}>
                            <a
                              href={`https://www.coupang.com/np/search?q=${encodeURIComponent(match.keyword)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '0.75rem',
                                color: '#007bff',
                                wordBreak: 'break-word',
                                textDecoration: 'underline',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {match.keyword}
                            </a>
                          </td>

                          {/* Glowpick Section */}
                          <td style={{ width: '50%', padding: '8px' }}>
                            <div className="d-flex gap-2 h-100">
                              {/* Glowpick Image */}
                              <div className="position-relative flex-shrink-0" style={{ width: '120px' }}>
                                <img
                                  src={match.gpDetailedProduct?.image || '/placeholder-image.png'}
                                  alt={match.gpDetailedProduct?.name || 'Glowpick Product'}
                                  className="rounded"
                                  style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'cover',
                                  }}
                                />
                                {/* Glowpick Overlay */}
                                {match.gpDetailedProduct?.url && (
                                  <a
                                    href={match.gpDetailedProduct.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-decoration-none"
                                    style={{
                                      background: 'rgba(0,0,0,0.45)',
                                      color: 'white',
                                      fontSize: '0.6rem',
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      borderRadius: '0.3rem',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.opacity = '0';
                                    }}
                                  >
                                    Glowpick ↗
                                  </a>
                                )}
                              </div>

                              {/* Glowpick Product Info */}
                              <div className="flex-grow-1 d-flex flex-column justify-content-between">
                                {/* Brand and Category */}
                                <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                                  <div className="d-flex align-items-center flex-wrap gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-decoration-none fw-semibold text-muted"
                                      style={{ fontSize: '0.85rem' }}
                                    >
                                      {match.gpDetailedProduct?.brand || 'Unknown Brand'}
                                    </button>
                                    {match.gpDetailedProduct?.subcategory && (
                                      <span className="ms-2" style={{ fontSize: '0.7rem', color: '#666666' }}>
                                        {match.gpDetailedProduct.category} › {match.gpDetailedProduct.subcategory}
                                      </span>
                                    )}
                                  </div>
                                  <span
                                    className="text-muted user-select-all d-flex align-items-center"
                                    style={{
                                      fontSize: '0.7rem',
                                      cursor: 'pointer',
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                    }}
                                    title="Glowpick Product ID"
                                  >
                                    {match.gpProductId}
                                  </span>
                                </div>

                                {/* Product Name */}
                                <h1
                                  className="fw-bold mb-1"
                                  style={{
                                    fontSize: '0.85rem',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {match.gpDetailedProduct?.name || 'Unknown Product'}
                                </h1>

                                {/* Size, Price, Awards */}
                                <div className="mb-1 d-flex align-items-center justify-content-between">
                                  <span style={{ fontSize: '0.75rem', color: '#666666' }}>
                                    {match.gpDetailedProduct?.size || 'N/A'} /{' '}
                                    {match.gpDetailedProduct?.price && match.gpDetailedProduct.price > 0
                                      ? `${match.gpDetailedProduct.price.toLocaleString()}KRW`
                                      : 'N/A'}
                                  </span>
                                  {match.gpDetailedProduct?.awards && match.gpDetailedProduct.awards.length > 0 && (
                                    <span
                                      style={{ color: '#ffd700', fontSize: '0.6rem' }}
                                      className="ms-1 d-flex align-items-center"
                                    >
                                      🏆
                                      <span className="ms-1 text-warning" style={{ fontSize: '0.7rem' }}>
                                        {match.gpDetailedProduct.awards[0]?.title?.replace(' 글로우픽 어워드', ' ') ||
                                          ''}
                                        {match.gpDetailedProduct.awards[0]?.category?.replace(' 부문', ' ') || ''}
                                        {match.gpDetailedProduct.awards[0]?.position?.replace(' 위', ' ') || 'Rookie'}
                                        {match.gpDetailedProduct.awards.length > 1 &&
                                          ` +${match.gpDetailedProduct.awards.length - 1}`}
                                      </span>
                                    </span>
                                  )}
                                </div>

                                {/* Rating, Reviews, Rankings */}
                                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                  <div className="d-inline-flex align-items-center flex-wrap gap-1">
                                    {/* Rating & Reviews */}
                                    {(match.gpDetailedProduct?.rating !== undefined &&
                                      match.gpDetailedProduct?.rating !== null) ||
                                    (match.gpDetailedProduct?.reviews !== undefined &&
                                      match.gpDetailedProduct?.reviews !== null) ? (
                                      <>
                                        <span className="me-1">
                                          {[...Array(5)].map((_, i) => (
                                            <Star
                                              key={i}
                                              size={9}
                                              className={`${i < Math.floor(match.gpDetailedProduct?.rating || 0) ? 'text-danger' : 'text-muted'}`}
                                              fill={
                                                match.gpDetailedProduct?.rating && match.gpDetailedProduct.rating > 0
                                                  ? 'currentColor'
                                                  : 'none'
                                              }
                                              style={{ marginRight: '1px' }}
                                            />
                                          ))}
                                        </span>
                                        <span className="me-1" style={{ fontSize: '0.75rem', color: '#333333' }}>
                                          {match.gpDetailedProduct?.rating || ''}
                                        </span>
                                        <span style={{ fontSize: '0.7rem', color: '#333333' }}>
                                          (
                                          {match.gpDetailedProduct?.reviews && match.gpDetailedProduct.reviews > 0
                                            ? match.gpDetailedProduct.reviews.toLocaleString()
                                            : 'No reviews'}
                                          )
                                        </span>
                                      </>
                                    ) : null}
                                  </div>

                                  {/* Rankings */}
                                  {((match.gpDetailedProduct?.rankings?.brand &&
                                    match.gpDetailedProduct.rankings.brand.ranking > 0) ||
                                    (match.gpDetailedProduct?.rankings?.category &&
                                      match.gpDetailedProduct.rankings.category.ranking > 0)) && (
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      {match.gpDetailedProduct?.rankings?.category &&
                                        match.gpDetailedProduct.rankings.category.ranking > 0 && (
                                          <span
                                            className="py-1 text-muted d-flex align-items-center"
                                            style={{ fontSize: '0.85rem' }}
                                          >
                                            <span style={{ marginRight: '2px' }}>🏅</span>
                                            {match.gpDetailedProduct.rankings.category.title ||
                                              match.gpDetailedProduct.category}{' '}
                                            <span className="text-danger ms-1">
                                              {match.gpDetailedProduct.rankings.category.ranking}위
                                            </span>
                                          </span>
                                        )}
                                      {match.gpDetailedProduct?.rankings?.brand &&
                                        match.gpDetailedProduct.rankings.brand.ranking > 0 && (
                                          <span
                                            className="px-2 py-1 text-muted d-flex align-items-center"
                                            style={{ fontSize: '0.85rem' }}
                                          >
                                            <span style={{ marginRight: '2px' }}>🏅</span>
                                            {match.gpDetailedProduct.rankings.brand.title ||
                                              match.gpDetailedProduct.brand}{' '}
                                            <span className="text-danger ms-1">
                                              {match.gpDetailedProduct.rankings.brand.ranking}위
                                            </span>
                                          </span>
                                        )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Coupang Section */}
                          <td style={{ width: '50%', padding: '8px' }}>
                            <div className="d-flex gap-2 h-100">
                              {/* Coupang Image */}
                              <div className="position-relative flex-shrink-0" style={{ width: '120px' }}>
                                <img
                                  src={match.cpDetailedProduct?.thumbnailImageUrl || '/placeholder-image.png'}
                                  alt={match.cpDetailedProduct?.name || 'Coupang Product'}
                                  className="rounded"
                                  style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'cover',
                                  }}
                                />
                                {/* Coupang Overlay */}
                                {match.cpDetailedProduct?.productPageUrl && (
                                  <a
                                    href={match.cpDetailedProduct?.productPageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-decoration-none"
                                    style={{
                                      background: 'rgba(0,0,0,0.45)',
                                      color: 'white',
                                      fontSize: '0.6rem',
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      borderRadius: '0.3rem',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.opacity = '0';
                                    }}
                                  >
                                    Coupang ↗
                                  </a>
                                )}
                              </div>

                              {/* Coupang Product Info */}
                              <div className="flex-grow-1 d-flex flex-column justify-content-between">
                                {/* Brand and Product ID */}
                                <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                                  <div className="d-flex align-items-center flex-wrap gap-1">
                                    <span className="fw-bold text-muted" style={{ fontSize: '0.85rem' }}>
                                      {match.cpDetailedProduct?.brand || ''}
                                    </span>
                                    {!match.cpDetailedProduct?.brand && (
                                      <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>
                                        3rd-Party Seller
                                      </Badge>
                                    )}
                                  </div>

                                  <span
                                    className="text-muted user-select-all d-flex align-items-center"
                                    style={{
                                      fontSize: '0.7rem',
                                      cursor: 'pointer',
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                    }}
                                    title="Coupang Product-Item ID"
                                  >
                                    {match.cpProductId}-{match.cpItemId}
                                  </span>
                                </div>
                                {match.cpDetailedProduct?.sellerInfo && (
                                  <div className="mb-1">
                                    <div
                                      className="d-flex align-items-center flex-wrap gap-2"
                                      style={{ fontSize: '0.75rem' }}
                                    >
                                      <div>
                                        <span className="text-muted">판매자:</span>
                                        <span className="fw-semibold text-dark">
                                          {match.cpDetailedProduct.sellerInfo.sellerName}
                                        </span>
                                      </div>
                                      {match.cpDetailedProduct.sellerInfo.sellerRating &&
                                        match.cpDetailedProduct.sellerInfo.sellerRating.trim() !== '' && (
                                          <span className="text-warning">
                                            ★ {match.cpDetailedProduct.sellerInfo.sellerRating}
                                          </span>
                                        )}
                                      {match.cpDetailedProduct.sellerInfo?.sellerReviewCount && (
                                        <span className="text-muted">
                                          (
                                          {Number(
                                            match.cpDetailedProduct.sellerInfo.sellerReviewCount,
                                          ).toLocaleString()}
                                          )
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Product Name */}
                                <div className="mb-1">
                                  <span style={{ fontSize: '0.85rem', lineHeight: '1.2', color: '#333333' }}>
                                    {match.cpDetailedProduct?.name && match.cpDetailedProduct.name.length > 60
                                      ? `${match.cpDetailedProduct?.name.substring(0, 60)}...`
                                      : match.cpDetailedProduct?.name || 'No product name'}
                                  </span>
                                </div>

                                {/* Pack/Item Name Information */}
                                {match.cpDetailedProduct?.options?.[0]?.itemName && (
                                  <div className="mb-1">
                                    <span
                                      className="badge bg-light text-dark border"
                                      style={{ fontSize: '0.7rem', fontWeight: '500' }}
                                      title={match.cpDetailedProduct.options[0].itemName}
                                    >
                                      {match.cpDetailedProduct.options[0].itemName.length > 20
                                        ? `${match.cpDetailedProduct.options[0].itemName.substring(0, 20)}...`
                                        : match.cpDetailedProduct.options[0].itemName}
                                    </span>
                                  </div>
                                )}

                                {/* Stock Status */}
                                {match.cpDetailedProduct?.outOfStock !== undefined && (
                                  <div className="mb-1">
                                    <Badge
                                      bg={match.cpDetailedProduct?.outOfStock ? 'danger' : 'success'}
                                      style={{ fontSize: '0.65rem' }}
                                    >
                                      {match.cpDetailedProduct?.outOfStock ? 'OUT OF STOCK' : ''}
                                    </Badge>
                                  </div>
                                )}

                                {/* Price, Unit Price, Rating, Shipping */}
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  {/* Price */}
                                  <span className="text-danger fw-bold" style={{ fontSize: '0.85rem' }}>
                                    {typeof match.cpDetailedProduct?.priceInfo?.salePrice === 'string'
                                      ? match.cpDetailedProduct?.priceInfo.salePrice
                                      : (match.cpDetailedProduct as any)?.salePrice
                                        ? `${(match.cpDetailedProduct as any).salePrice.toLocaleString()}원`
                                        : (match.cpDetailedProduct as any)?.unitPrice
                                          ? `${(match.cpDetailedProduct as any).unitPrice.toLocaleString()}원`
                                          : 'N/A'}
                                  </span>

                                  {/* Unit Price */}
                                  {(match.cpDetailedProduct?.priceInfo?.unitPrice ||
                                    match.cpDetailedProduct?.options?.[0]?.unitPrice) && (
                                    <span className="text-danger" style={{ fontSize: '0.75rem' }}>
                                      {match.cpDetailedProduct?.priceInfo?.unitPrice ||
                                        match.cpDetailedProduct?.options?.[0]?.unitPrice}
                                    </span>
                                  )}

                                  {/* Rating */}
                                  {match.cpDetailedProduct?.rating && match.cpDetailedProduct?.rating > 0 && (
                                    <div className="d-flex align-items-center">
                                      <Star size={10} className="text-warning me-1" fill="currentColor" />
                                      <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>
                                        {match.cpDetailedProduct?.rating}
                                      </span>
                                      {match.cpDetailedProduct?.reviews && match.cpDetailedProduct?.reviews > 0 && (
                                        <small className="text-muted ms-1" style={{ fontSize: '0.65rem' }}>
                                          ({match.cpDetailedProduct?.reviews.toLocaleString()})
                                        </small>
                                      )}
                                    </div>
                                  )}

                                  {/* Shipping */}
                                  <div className="d-flex align-items-center mt-1">
                                    {match.cpDetailedProduct?.shippingInfo?.shippingType ? (
                                      // New shippingInfo structure
                                      <>
                                        {match.cpDetailedProduct?.shippingInfo?.shippingType ===
                                          ShippingType.ROCKET && (
                                          <img
                                            src="/assets/rocket-logo.png"
                                            alt="Rocket"
                                            style={{ height: '16px', width: 'auto' }}
                                          />
                                        )}
                                        {match.cpDetailedProduct?.shippingInfo?.shippingType ===
                                          ShippingType.SELLER_ROCKET && (
                                          <img
                                            src="/assets/seller-rocket-logo.png"
                                            alt="Seller Rocket"
                                            style={{ height: '16px', width: 'auto' }}
                                          />
                                        )}
                                        {match.cpDetailedProduct?.shippingInfo?.shippingType ===
                                          ShippingType.SELLER_DELIVERY && (
                                          <span style={{ fontSize: '0.75rem' }}>판매자배송</span>
                                        )}
                                      </>
                                    ) : (
                                      // Legacy shipping field
                                      <>
                                        {match.cpDetailedProduct?.shipping === 'Rocket' && (
                                          <img
                                            src="/assets/rocket-logo.png"
                                            alt="Rocket"
                                            style={{ height: '16px', width: 'auto' }}
                                          />
                                        )}
                                        {match.cpDetailedProduct?.shipping === 'Seller Rocket' && (
                                          <img
                                            src="/assets/seller-rocket-logo.png"
                                            alt="Seller Rocket"
                                            style={{ height: '16px', width: 'auto' }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Actions Column */}
                          <td
                            style={{ width: '140px', textAlign: 'center', verticalAlign: 'middle', padding: '8px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="d-flex flex-column gap-1">
                              {/* Go to Matching button */}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (match.gpProductId) {
                                    navigate(ROUTES.sourcing.matchingWithGlowpickId(match.gpProductId));
                                  }
                                }}
                                disabled={!match.gpProductId || isUnmatched}
                                title={isUnmatched ? 'Item is unmatched' : 'Go to GP-CP Matching page'}
                                style={{
                                  fontSize: '0.7rem',
                                  padding: '3px 6px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Go to Matching
                              </Button>

                              {/* Unmatch button or Unmatched badge */}
                              {unmatchedItems.has(matchKey) ? (
                                <Badge bg="secondary" style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
                                  ✓ Unmatched
                                </Badge>
                              ) : (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleIndividualUnmatch(match);
                                  }}
                                  disabled={isBulkUnmatching}
                                  title="Unmatch this product"
                                  style={{
                                    fontSize: '0.7rem',
                                    padding: '3px 6px',
                                  }}
                                >
                                  <X size={12} className="me-1" />
                                  Unmatch
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Load More Button */}
              {pagination.hasNextPage && (
                <div className="text-center p-3 border-top">
                  <Button
                    variant="outline-primary"
                    onClick={loadMore}
                    disabled={loading}
                    className="d-flex align-items-center mx-auto"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}

              {/* Results info */}
              <div className="px-3 py-2 bg-light border-top small text-muted">
                Showing {matchedProducts.length} matched products
                {pagination.hasNextPage && ' (more available)'}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Custom CSS for spin animation */}
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .list-group-item-action:hover {
            background-color: #f8f9fa !important;
          }
        `}
      </style>
    </Container>
  );
};

export default MatchedProductsPage;
